import React, { useState, useEffect } from 'react';
import './QRCodeGenerator.css';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useAuth } from '../contexts/AuthContext';
import authenticatedFetch from '../utils/authenticatedFetch';

interface Enterprise {
  enterpriseId: string;
  name: string;
}

interface Store {
  storeId: string;
  name: string;
  storeCode?: string;
  code?: string; // Backward compatibility
  address?: string;
  enterpriseId: string;
}

interface QRCodeData {
  storeCode: string;
  url: string;
  qrCodeDataUrl: string;
}

const SURVEY_BASE_URL = 'https://bigsystemone.com/s/store';

const QRCodeGenerator: React.FC = () => {
  const { getIdToken } = useAuth();
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState<string>('');
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('all');
  const [qrCodes, setQrCodes] = useState<{[key: string]: QRCodeData}>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [qrType, setQrType] = useState<'menu' | 'survey'>('menu');

  // Load enterprises on mount
  useEffect(() => {
    loadEnterprises();
  }, []);

  // Load stores when enterprise changes
  useEffect(() => {
    if (selectedEnterpriseId) {
      loadStores();
    } else {
      setStores([]);
    }
  }, [selectedEnterpriseId]);

  const loadEnterprises = async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.enterprises());
      if (response.ok) {
        const data = await response.json();
        const enterpriseList = data.data || data || [];
        setEnterprises(enterpriseList);
        // Auto-select first enterprise if available
        if (enterpriseList.length > 0 && !selectedEnterpriseId) {
          setSelectedEnterpriseId(enterpriseList[0].enterpriseId);
        }
      }
    } catch (error) {
      console.error('Error loading enterprises:', error);
      setMessage('Error loading enterprises');
    }
  };

  const loadStores = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(
        API_ENDPOINTS.enterpriseStores(selectedEnterpriseId)
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[QRCodeGenerator] Stores API response:', data);
        // Handle both array response and wrapped response formats
        const storesData = Array.isArray(data) ? data : (data.data || data.stores || []);
        console.log('[QRCodeGenerator] Parsed stores:', storesData);
        setStores(storesData);
        setSelectedStoreId('all'); // Reset store selection when enterprise changes
      } else {
        console.error('[QRCodeGenerator] Failed to load stores:', response.status, response.statusText);
        setMessage('Failed to load stores');
        setStores([]);
      }
    } catch (error) {
      console.error('Error loading stores:', error);
      setMessage('Error loading stores');
      setStores([]);
    } finally {
      setLoading(false);
    }
  };

  // Get filtered stores based on selection
  const getFilteredStores = (): Store[] => {
    if (selectedStoreId === 'all') {
      return stores;
    }
    return stores.filter(store => store.storeId === selectedStoreId);
  };

  const generateQRCode = async (store: Store) => {
    try {
      setLoading(true);

      // Determine the store code - try multiple possible field names
      const storeCode = store.storeCode || store.code || store.storeId || 'unknown';

      // Generate URL based on QR type
      let targetUrl: string;
      if (qrType === 'survey') {
        targetUrl = `${SURVEY_BASE_URL}/${store.storeId}`;
      } else {
        const baseUrl = window.location.origin;
        targetUrl = `${baseUrl}/menu/${storeCode}`;
      }

      // Generate QR code using QR Server API (free service)
      const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(targetUrl)}`;

      const qrData: QRCodeData = {
        storeCode: storeCode,
        url: targetUrl,
        qrCodeDataUrl: qrApiUrl
      };

      setQrCodes(prev => ({
        ...prev,
        [store.storeId]: qrData
      }));

      const typeLabel = qrType === 'survey' ? 'Review' : 'Menu';
      setMessage(`${typeLabel} QR Code generated for ${store.name} (Code: ${storeCode})`);

    } catch (error) {
      console.error('Error generating QR code:', error);
      setMessage('Error generating QR code');
    } finally {
      setLoading(false);
    }
  };

  const generateAllQRCodes = async () => {
    try {
      setLoading(true);
      const filteredStores = getFilteredStores();
      setMessage(`Generating QR codes for ${filteredStores.length} store(s)...`);

      for (const store of filteredStores) {
        await generateQRCode(store);
      }

      setMessage(`Generated QR codes for ${filteredStores.length} store(s)`);
    } catch (error) {
      console.error('Error generating QR codes:', error);
      setMessage('Error generating QR codes');
    } finally {
      setLoading(false);
    }
  };

  const downloadQRCode = async (storeId: string, storeName: string) => {
    const qrData = qrCodes[storeId];
    if (!qrData) return;

    const isSurvey = qrType === 'survey';
    const callToAction = isSurvey ? 'Scan to Leave a Review' : 'Scan for Menu';
    const filePrefix = isSurvey ? 'qr-review' : 'qr-menu';

    try {
      // Create a canvas to add text to the QR code
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // Set canvas size (QR code + space for text)
        canvas.width = 400;
        canvas.height = 400;

        // Fill white background
        ctx!.fillStyle = 'white';
        ctx!.fillRect(0, 0, canvas.width, canvas.height);

        // Draw QR code centered
        const qrSize = 300;
        const x = (canvas.width - qrSize) / 2;
        const y = 20;
        ctx!.drawImage(img, x, y, qrSize, qrSize);

        // Add store name text
        ctx!.fillStyle = 'black';
        ctx!.font = 'bold 18px Arial';
        ctx!.textAlign = 'center';
        ctx!.fillText(storeName, canvas.width / 2, 350);

        // Add call-to-action text
        ctx!.font = '14px Arial';
        ctx!.fillText(callToAction, canvas.width / 2, 375);

        // Download the image
        const link = document.createElement('a');
        link.download = `${filePrefix}-${storeName.replace(/\s+/g, '-').toLowerCase()}.png`;
        link.href = canvas.toDataURL();
        link.click();
      };

      img.src = qrData.qrCodeDataUrl;

    } catch (error) {
      console.error('Error downloading QR code:', error);
      setMessage('Error downloading QR code');
    }
  };

  const copyMenuUrl = (storeId: string) => {
    const qrData = qrCodes[storeId];
    if (!qrData) return;

    navigator.clipboard.writeText(qrData.url).then(() => {
      setMessage('Menu URL copied to clipboard');
    }).catch(() => {
      setMessage('Failed to copy URL');
    });
  };

  const printQRCode = (storeId: string, storeName: string) => {
    const qrData = qrCodes[storeId];
    if (!qrData) return;

    const isSurvey = qrType === 'survey';
    const title = isSurvey ? `QR Review - ${storeName}` : `QR Menu - ${storeName}`;
    const instruction = isSurvey
      ? 'Scan with your phone to leave a review'
      : 'Scan with your phone to view our menu';

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            text-align: center;
            padding: 2rem;
            margin: 0;
          }
          .qr-container {
            max-width: 400px;
            margin: 0 auto;
            border: 2px solid #333;
            padding: 2rem;
            border-radius: 10px;
          }
          h1 {
            color: #333;
            margin-bottom: 1rem;
            font-size: 24px;
          }
          .qr-code {
            margin: 1rem 0;
          }
          .instructions {
            color: #666;
            font-size: 16px;
            margin-top: 1rem;
          }
          .url {
            font-size: 12px;
            color: #999;
            margin-top: 1rem;
            word-break: break-all;
          }
          @media print {
            body { margin: 0; }
            .qr-container { border-color: #000; }
          }
        </style>
      </head>
      <body>
        <div class="qr-container">
          <h1>${storeName}</h1>
          <div class="qr-code">
            <img src="${qrData.qrCodeDataUrl}" alt="QR Code for ${storeName}" style="width: 300px; height: 300px;" />
          </div>
          <div class="instructions">
            <strong>${instruction}</strong>
          </div>
          <div class="url">${qrData.url}</div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  };

  const filteredStores = getFilteredStores();

  return (
    <div className="qr-code-generator">
      <div className="modern-card">
        <h2>QR Code Generator</h2>
        <p>
          {qrType === 'menu'
            ? 'Generate QR codes for each store\'s digital menu. Customers can scan to view the menu with current pricing.'
            : 'Generate review QR codes for table tents, receipts, or signage. Customers scan to leave a quick rating.'}
        </p>

        {/* QR Type Toggle */}
        <div className="qr-type-toggle">
          <button
            className={`qr-type-btn ${qrType === 'menu' ? 'active' : ''}`}
            onClick={() => { setQrType('menu'); setQrCodes({}); }}
          >
            Menu QR
          </button>
          <button
            className={`qr-type-btn ${qrType === 'survey' ? 'active' : ''}`}
            onClick={() => { setQrType('survey'); setQrCodes({}); }}
          >
            Review QR
          </button>
        </div>

        {message && (
          <div className={`alert ${message.includes('Error') || message.includes('Failed') ? 'alert-error' : 'alert-success'}`}>
            {message}
          </div>
        )}

        {/* Enterprise and Store Selectors */}
        <div className="selector-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ flex: '1', minWidth: '200px' }}>
            <label htmlFor="enterprise-select">Enterprise</label>
            <select
              id="enterprise-select"
              value={selectedEnterpriseId}
              onChange={(e) => setSelectedEnterpriseId(e.target.value)}
              className="form-control"
            >
              <option value="">Select Enterprise</option>
              {enterprises.map(enterprise => (
                <option key={enterprise.enterpriseId} value={enterprise.enterpriseId}>
                  {enterprise.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ flex: '1', minWidth: '200px' }}>
            <label htmlFor="store-select">Store</label>
            <select
              id="store-select"
              value={selectedStoreId}
              onChange={(e) => setSelectedStoreId(e.target.value)}
              className="form-control"
              disabled={!selectedEnterpriseId || stores.length === 0}
            >
              <option value="all">All Stores ({stores.length})</option>
              {stores.map(store => (
                <option key={store.storeId} value={store.storeId}>
                  {store.name} {store.storeCode ? `(${store.storeCode})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="generator-controls">
          <button
            onClick={generateAllQRCodes}
            disabled={loading || filteredStores.length === 0 || !selectedEnterpriseId}
            className="btn btn-modern-primary"
          >
            {loading ? 'Generating...' : `Generate QR Code${filteredStores.length !== 1 ? 's' : ''} (${filteredStores.length})`}
          </button>
        </div>

        <div className="stores-grid">
          {filteredStores.map(store => {
            const qrData = qrCodes[store.storeId];
            
            return (
              <div key={store.storeId} className="store-qr-card">
                <div className="store-info">
                  <h3>{store.name}</h3>
                  <p>Store Code: {store.storeCode || store.code || 'Not Set'}</p>
                  {store.address && <p className="store-address">{store.address}</p>}
                </div>

                <div className="qr-actions">
                  {!qrData ? (
                    <button
                      onClick={() => generateQRCode(store)}
                      disabled={loading}
                      className="btn btn-modern-secondary"
                    >
                      Generate QR Code
                    </button>
                  ) : (
                    <div className="qr-display">
                      <div className="qr-image">
                        <img 
                          src={qrData.qrCodeDataUrl} 
                          alt={`QR Code for ${store.name}`}
                          className="qr-code-img"
                        />
                      </div>
                      
                      <div className="menu-url">
                        <small>{qrType === 'survey' ? 'Review URL:' : 'Menu URL:'}</small>
                        <code>{qrData.url}</code>
                      </div>
                      
                      <div className="qr-buttons">
                        <button
                          onClick={() => downloadQRCode(store.storeId, store.name)}
                          className="btn btn-outline btn-sm"
                          title="Download QR Code"
                        >
                          Download
                        </button>
                        
                        <button
                          onClick={() => printQRCode(store.storeId, store.name)}
                          className="btn btn-outline btn-sm"
                          title="Print QR Code"
                        >
                          Print
                        </button>
                        
                        <button
                          onClick={() => copyMenuUrl(store.storeId)}
                          className="btn btn-outline btn-sm"
                          title="Copy Menu URL"
                        >
                          Copy URL
                        </button>
                        
                        <a
                          href={qrData.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline btn-sm"
                          title="Preview Menu"
                        >
                          Preview
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!selectedEnterpriseId && !loading && (
          <div className="empty-state">
            <p>Please select an enterprise to view stores.</p>
          </div>
        )}

        {selectedEnterpriseId && filteredStores.length === 0 && !loading && (
          <div className="empty-state">
            <p>No stores found for the selected enterprise. Please add stores first.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodeGenerator;